import { Injectable, inject, signal } from '@angular/core';
import { combineLatest, firstValueFrom } from 'rxjs';
import { LoggingService } from './logging.service';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { AuthService, IdToken, User } from '@auth0/auth0-angular';
import { GcmNotificationService, Notification, NotificationType } from '@gcm/gcm-ui-angular';
import { EnvironmentVariablesService } from './environment-variables.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private loggingService = inject(LoggingService);
  private cookieService = inject(CookieService);
  router = inject(Router);
  private notificationService = inject(GcmNotificationService);
  private auth = inject(AuthService);
  private evService = inject(EnvironmentVariablesService);

  public user = signal<User | null>(null);
  public token = signal<IdToken | null>(null);
  public authenticated = signal<boolean>(false);


  constructor() {
    combineLatest([
      this.auth.user$,
      this.auth.isAuthenticated$,
      this.auth.idTokenClaims$
    ]).subscribe(([user, isAuth, idToken]) => {
      this.user.set(user ?? null);
      this.token.set(idToken ?? null);
      this.authenticated.set(isAuth);
    });
  }

  logoutApp(): void {
    const userEmail = this.user()?.email || 'User';
    this.loggingService.log(`${userEmail} - Logging out`);
    this.deleteCustomCookies();
    this.auth.logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  }

  getUserEmail(): string {
    return this.user()?.email ?? '';
  }

  getUserFullName(): string {
    return this.user()?.name ?? '';
  }

  private get audience() { return this.evService.auth0Config.audience; }

  isExternalUser(): boolean { return !this.user()?.[`urn:${this.audience}:isInternalUser`]?.[0] }

  isAdmin(): boolean { return this.user()?.[`urn:${this.audience}:admin`]?.[0]; }

  getApplicationAccess(): string[] { return this.user()?.assignedApplications ?? []; }

  getInvestorIds(): number[] {
    const access = this.token()?.[`urn:${this.audience}:access`];
    if (!access) return [];

    // Each entry is in the format "investorId:*:*"
    return (access as string[]).map((entry) => parseInt(entry.split(':')[0], 10))
      .filter((id) => !isNaN(id));
  }

  setCookieValue(key: string, value: string) {
    this.cookieService.set(key, value, { path: "/" });
  }

  getCookieValue(key: string): string {
    return this.cookieService.get(key);
  }

  deleteCookieValue(key: string) {
    this.cookieService.delete(key);
  }

  async refreshIdToken() {
    // Force a fresh token from Auth0, bypassing cache
    await firstValueFrom(this.auth.getAccessTokenSilently({ cacheMode: 'off' }));

    // Now pull the updated claims
    const [user, idToken, isAuth] = await firstValueFrom(
      combineLatest([this.auth.user$, this.auth.idTokenClaims$, this.auth.isAuthenticated$])
    );

    this.user.set(user ?? null);
    this.token.set(idToken ?? null);
    this.authenticated.set(isAuth);
  }

  checkIfSessionExistsAndLogoutIfExpired(): void {
    // Attempt to refresh the token - if the session is expired, this will fail
    this.refreshIdToken()
      .catch(() => {
        this.showSessionExpiredToast();
        this.logoutApp();
      });
  }

  showSessionExpiredToast() {
    this.notificationService.notify(new Notification("Your session has expired. Please login", "Error", NotificationType.error));
  }

  userIsOnInvestorDashboard(): boolean {
    return ['investor'].indexOf(this.router.url.split('?')[0].toLowerCase().split('/').slice(-1)[0]) > -1;
  }

  doesUserHaveSEIAccess() {
    const assignedInvestorIds = this.getInvestorIds();
    const SEIPortfolioInvestorIds = [1202, 5681, 5856, 5895, 6089, 7341, 8152, 8516, 8578, 8874, 9004, 9353, 9792, 9793, 9963, 10152, 10714, 10833, 10834, 11133, 11136, 11138, 11141, 11381, 11385, 11485, 12007, 12273, 12571, 12572, 12573, 12603, 12604, 12606, 12679, 12923, 12969, 13212, 13216, 13244, 13256, 15403, 15420, 15432, 15433, 15437, 15438, 15439, 15463, 15465, 15475, 15477, 15478, 15480, 15481, 15484, 15488, 15491, 15517, 15542, 15550, 15551, 15552, 15553, 15554, 15555, 15556, 15557, 15558, 15559, 15560, 15561, 15562, 15563, 15565, 15566, 15567, 15568, 15569, 15570, 15571, 15572, 15573, 15809, 15845, 15856, 15857, 15859, 15860, 15863, 15865, 15871, 15872, 15873, 15874, 15877, 15879, 15890, 15891, 15892, 15897, 15900, 15901, 15903, 15904, 15905, 15907, 15911, 15920, 15921, 15922, 15941, 15942, 15947, 15948, 15951, 15952, 15953, 15954, 15955, 15956, 15957, 15958, 15959, 15960, 15962, 15963, 15964, 15965, 15966, 15967, 15968, 15970, 15971, 15973, 15974, 15975, 15977, 15979, 15980, 15982, 15983, 15984, 15985, 15986, 20001, 20004, 20059, 20069, 20070, 20071, 20073, 20075, 20076, 20077, 20078, 20079, 20081, 20082, 20083, 20084, 20085, 20086, 20089, 20091, 20095, 20097, 20098, 20101, 20102, 20104, 20105, 20106, 20107, 20109, 20112, 20113, 20114, 20115, 20116, 20117, 20127, 20131, 20133, 20137, 20138, 20139, 20140, 20141, 20142, 20143, 20144, 20145, 20146, 20147, 20148, 20149, 20150, 20151, 20152, 20153, 20154, 20155, 20156, 20158, 20159, 20160, 20161, 20162, 20172, 20173, 20174, 20175, 20203, 20209, 20210, 20212, 20224, 20225, 20226, 20230, 20232, 20234, 20235, 20243, 20245, 20246, 20247, 20249, 20254, 20255, 20256, 20257, 20258, 20259, 20268, 20275, 20300, 20301, 20308, 20309, 20310, 20320, 20321, 20323, 20346, 20349, 20355, 20360, 20361, 20362, 20363, 20364, 20371, 20374, 20376, 20379, 20380, 20381, 20384, 20385, 20386, 20388, 20394, 20412, 20440, 20442, 20448, 20449, 20477, 20478, 20512, 20522, 20523, 20524, 20548, 20568, 20569, 20571, 20576, 20578, 20586, 20587, 20588, 20591, 20592, 20606, 20611, 20629, 20636, 20641, 20645, 20647, 20674, 20677, 20682, 20710, 20712, 20714, 20715, 20717, 20718, 20722, 20723, 20724, 20725, 20726, 20727, 20730, 20731, 20732, 20734, 20735, 20739, 20750, 20752, 20753, 20755, 20765, 20769, 20774, 20775, 20792, 20800, 20801, 20802, 20803, 20810, 20813, 20814, 20817, 20823, 20825, 20828, 20829, 20830, 20831, 20833, 20852, 20853, 20872, 20873, 20874, 20875, 20877, 20878, 20879, 20880, 20970, 20973, 20985, 20987, 20988, 20989, 20990, 20991, 20994, 20995, 20996, 20997, 20998, 20999, 21013, 21014, 21015, 21016, 21017, 21018, 21019, 21021, 21022, 21023, 21024, 21025, 21026, 21029, 21030, 21031, 21032, 21033, 21034, 21035, 21036, 21037, 21038, 21039, 21040, 21041, 21042, 21043, 21044, 21045, 21046, 21047, 21048, 21049, 21050, 21051, 21052, 21053, 21055, 21056, 21057, 21058, 21059, 21061, 21062, 21064, 21079, 21086, 21090, 21097, 21100, 21101, 21102, 21106, 21107, 21110, 21112, 21115, 21117, 21118, 21119, 21121, 21124, 21125, 21126, 21129, 21134, 21135, 21136, 21138, 21139, 21140, 21145, 21147, 21150, 21152, 21155, 21156, 21157, 21158, 21166, 21169, 21170, 21175, 21181, 21182, 21185, 21186, 21193, 21195, 21196, 21199, 21218, 21223, 21235, 21241, 21242, 21256, 21257, 21258, 21271, 21278, 21279, 21280, 21281, 21282, 21283, 21286, 21287, 21288, 21289, 21290, 21291, 21292, 21294, 21295, 21296, 21297, 21298, 21299, 21300, 21301, 21307, 21308, 21311, 21313, 21319, 21325, 21326, 21327, 21328, 21330, 21331, 21332, 21369, 21370, 21375, 21377, 21381, 21382, 21384, 21386, 21387, 21393, 21394, 21399, 21400, 21401, 21403, 21410, 21418, 21419, 21423, 21430, 21432, 21439, 21441, 21442, 21443, 21444, 21451, 21452, 21454, 21458, 21462, 21463, 21464, 21467, 21468, 21469, 21470, 21475, 21476, 21477, 21485, 21486, 21487, 21488, 21489, 21491, 21494, 21498, 21502, 21504, 21506, 21507, 21508, 21510, 21513, 21514, 21517, 21536, 21538, 21542, 21543, 21559, 21563, 21697, 21698, 21700, 21704, 21710, 21716, 21718, 21750, 21751, 21753, 21758, 21768, 21769, 21771, 21772, 21773, 21783, 21784, 21785, 21786, 21787, 21790, 21791, 21793, 21794, 21795, 21796, 21799, 21801, 21802, 21804, 21807, 21808, 21810, 21813, 21814, 21815, 21816, 21818, 21819, 21826, 21844, 21846, 21848, 21857, 21860, 21862, 21863, 21864, 21865, 21867, 21868, 21869, 21871, 21873, 21875, 21878, 21879, 21881, 21882, 21915, 21916, 21917, 21919, 21921, 21922, 21923, 21924, 21925, 21926, 21927, 21928, 21929, 21930, 21931, 21932, 21933, 21935, 21936, 21938, 21943, 21944, 21945, 21946, 21947, 21948, 21949, 21950, 21965, 21968, 21969, 21971, 21974, 21977, 21978, 21979, 21980, 21981, 21982, 21983, 21989, 21999, 22003, 22004, 22005, 22006, 22007, 22008, 22009, 22011, 22012, 22013, 22014, 22015, 22016, 22017, 22020, 22021, 22022, 22023, 22024, 22026, 22027, 22028, 22029, 22030, 22031, 22032, 22033, 22034, 22036, 22037, 22038, 22040, 22051, 22055, 22056, 22057, 22058, 22061, 22062, 22063, 22064, 22065, 22066, 22067, 22068, 22069, 22071, 22073, 22075, 22076, 22077, 22079, 22082, 22084, 22091, 22092, 22102, 22108, 22109, 22110, 22111, 22112, 22113, 22114, 22116, 22117, 22119, 22189, 22199, 22200, 22204, 22208, 22209, 22270, 22271, 22279, 22280, 22281, 22282, 22283, 22284, 22285, 22286, 22287, 22293, 22295, 22296, 22301, 22309, 22310, 22311, 22312, 22329, 22336, 22337, 22338, 22346, 22348, 22362, 22414, 22417, 22418, 22460, 22462, 22487, 22489, 22501, 22503, 22509, 22511, 22512, 22513, 22514, 22613, 22614, 99949, 99950, 99951, 99952, 99956, 99957, 99958, 99959, 99962, 99963, 99964, 99965, 99969, 99970, 99971, 99972, 99973, 99975, 99981, 99982, 99983, 99984, 99985, 99986, 99993, 99994, 99995, 99996, 99997, 99998, 99999]
    return assignedInvestorIds.some(id => SEIPortfolioInvestorIds.includes(id))
  }

  deleteCustomCookies() {
    this.cookieService.delete('selectedInvestorId');
    this.cookieService.delete('dashboardSource');
    this.cookieService.delete('dashboardPortfolioId');
  }
}
